import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { Link } from "react-router-dom";

// Store
import { useAPIStore } from "../../../Store/APIStore";
import { pathTitles, slowScrollTo, useStore } from "../../../Store/Store";
// Components
import {
  BorderAll,
  Carousel,
  ChevronLeft,
  ChevronRight,
  SEOHead,
} from "../../Common";
// Profile Components
import { InvestmentBreakdown } from "./InvestmentBreakdown";
// import { MediaGallery } from "./MediaGallery";
import { PersonalityProfile } from "./PersonalityProfile";
import { PuppyHero } from "./PuppyHero";
import { PuppyOverview } from "./PuppyOverview";
import { TrainingDetails } from "./TrainingDetails";

export const EnhancedPuppyProfile = ({ puppy }) => {
  const [activeSection, setActiveSection] = useState("overview");
  const { puppies, fetchPuppies, loading, puppyCounts } = useAPIStore();
  const getLink = useStore((state) => state.getLink);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fetch puppies to ensure we have complete navigation data
  useEffect(() => {
    fetchPuppies(); // Always fetch to ensure navigation works
  }, [fetchPuppies]);

  useEffect(() => {
    setIsLoaded(!loading);
  }, [loading]);

  // Calculate navigation based on current puppy position in API data
  const currentIndex =
    puppies.length > 0
      ? puppies.findIndex((p) => p.profilePath === puppy.profilePath)
      : -1;

  const showLeft = currentIndex > 0 ? "show" : "";
  const showRight =
    currentIndex >= 0 && currentIndex + 1 < puppyCounts.total ? "show" : "";

  // Disable navigation while loading or if no valid paths
  const isLeftDisabled = isLoaded || !showLeft;
  const isRightDisabled = isLoaded || !showRight;

  const pathLeft =
    showLeft && puppies[currentIndex - 1]
      ? `${getLink(pathTitles.VIEWPUPPIES)}${puppies[currentIndex - 1].profilePath}`
      : "";
  const pathRight =
    showRight && puppies[currentIndex + 1]
      ? `${getLink(pathTitles.VIEWPUPPIES)}${puppies[currentIndex + 1].profilePath}`
      : "";

  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Smooth scroll to the content section
    const contentElement = document.getElementById("puppy-content");
    if (contentElement) {
      slowScrollTo(contentElement);
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case "overview":
        return <PuppyOverview puppy={puppy} />;
      case "training":
        return <TrainingDetails puppy={puppy} />;
      case "personality":
        return <PersonalityProfile puppy={puppy} />;
      case "gallery":
        // return <MediaGallery puppy={puppy} />;
        return (
          <>
            {puppy.carouselOptimized && puppy.carouselOptimized.length > 0 ? (
              <Carousel
                optimizedImages={puppy.carouselOptimized}
                size="large"
              />
            ) : puppy.carousel ? (
              <Carousel images={puppy.carousel} />
            ) : null}
          </>
        );
      case "investment":
        return <InvestmentBreakdown puppy={puppy} />;
      default:
        return <PuppyOverview puppy={puppy} />;
    }
  };

  return (
    <div className="enhanced-puppy-profile">
      {/* SEO Head for this specific puppy */}
      <SEOHead
        pageType="puppy"
        puppy={puppy}
        canonical={`${window.location.origin}/puppies${puppy.profilePath}`}
      />

      {/* Navigation Header */}
      <Container className="puppy-navigation-header">
        <div className="navigation-controls">
          <Button
            className={`transparent chevron-left ${showLeft}`}
            as={Link}
            to={pathLeft}
            disabled={isLeftDisabled}
          >
            {showLeft && <ChevronLeft className={`chevron-left ${showLeft}`} />}
          </Button>

          <Button
            className="transparent back-to-all"
            as={Link}
            to={getLink(pathTitles.VIEWPUPPIES)}
            title="Back to all puppies"
          >
            <BorderAll className="border-all" />
          </Button>

          <Button
            className={`transparent chevron-right ${showRight}`}
            as={Link}
            to={pathRight}
            disabled={isRightDisabled}
          >
            <ChevronRight className={`chevron-right ${showRight}`} />
          </Button>
        </div>
      </Container>

      {/* Hero Section */}
      <PuppyHero
        puppy={puppy}
        onSectionChange={handleSectionChange}
        activeSection={activeSection}
      />

      {/* Content Section */}
      <div id="puppy-content" className="puppy-content">
        <Container>
          <div className="section-content">{renderActiveSection()}</div>
        </Container>
      </div>

      {/* Floating Contact Bar */}
      <div className="floating-contact-bar">
        <Container>
          <div className="contact-bar-content">
            <div className="contact-info">
              <span className="puppy-name">
                {puppy.cardHeaderTitle} -{" "}
                {puppy.available ? "✅ Available Now" : "Sold"}
              </span>
              {puppy.available &&
                puppy.price &&
                puppy.price !== "unavailable" && (
                  <span
                    className="puppy-price"
                    style={{ marginLeft: "15px", fontWeight: "600" }}
                  >
                    {puppy.price}
                  </span>
                )}
            </div>
            <div className="contact-actions">
              <span className="phone-number">(317) 954-6952</span>
              <a
                href={`sms:+13179546952?body=Hi Justine! I'm interested in ${puppy.cardHeaderTitle}. Can we schedule a call?`}
                className="contact-button"
              >
                📱 Schedule a Call
              </a>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

EnhancedPuppyProfile.propTypes = {
  puppy: PropTypes.object.isRequired,
};
